// CabinInside
// Features:
// - Renders interior cabin mesh with custom shader (lights enabled)
// - Includes physics bounds for the room
// - Manages interior lighting state and passing controls to Props
// - Instantiates Door and RoomLights

import { useMemo } from 'react'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'
import { getPointLightUniforms } from '../../utils/lighting'

import Props from '../Props/Props'
import Door from './Door'
import RoomLights from './RoomLights'

import vertexShaderInside from '../shaders/cabin/inside/vertex.glsl'
import fragmentShaderInside from '../shaders/cabin/inside/fragment.glsl'

const LIGHT_CONFIG = {
    // Point Light 1
    p1PosX: 0.18,
    p1PosY: 2.97,
    p1PosZ: -1.89,
    p1Color: '#e6bf71',
    p1Intensity: 1.87,
    p1Decay: 0.33,

    // Point Light 2
    p2PosX: -0.09,
    p2PosY: 2.58,
    p2PosZ: 2.71,
    p2Color: '#e6bf71',
    p2Intensity: 1.94,
    p2Decay: 0.42,

    // Point Light 3
    p3PosX: -2.54,
    p3PosY: 2.82,
    p3PosZ: 1.69,
    p3Color: '#e6bf71',
    p3Intensity: 1.29,
    p3Decay: 0.48,

    // Common
    pSpecularPower: 1.0,
    ambientIntensity: 0.5,
}

export default function CabinInside({ nodes, cabinTexture, physicsMaterial }) {
    const lightsOn = useStore((state) => state.lightsOn)

    // Materials
    // Interior Shader Material
    const materialInside = useMemo(() => {
        const pointFactor = lightsOn ? 1.0 : 0.1
        const ambientFactor = lightsOn ? 1.0 : 0.2

        return new THREE.ShaderMaterial({
            uniforms: {
                uColor: new THREE.Uniform(new THREE.Color('#ff0000')),
                uMap: { value: cabinTexture },
                uIntensity: {
                    value: LIGHT_CONFIG.ambientIntensity * ambientFactor,
                },
                ...getPointLightUniforms(LIGHT_CONFIG, pointFactor),
            },
            vertexShader: vertexShaderInside,
            fragmentShader: fragmentShaderInside,
        })
    }, [cabinTexture, lightsOn])

    return (
        <group>
            {/* Visual Mesh */}
            <mesh
                geometry={nodes.cabin_in.geometry}
                material={materialInside}
            />

            {/* Physics Bounds */}
            <RigidBody
                colliders="trimesh"
                type="fixed"
                userData={{ name: 'room_bounds' }}
            >
                <mesh
                    geometry={nodes.physics_bounds.geometry}
                    material={physicsMaterial}
                />
            </RigidBody>

            {/* Components */}
            <Door nodes={nodes} material={materialInside} />
            <Props
                nodes={nodes}
                lightControls={LIGHT_CONFIG}
                physicsMaterial={physicsMaterial}
            />
            <RoomLights nodes={nodes} />
        </group>
    )
}
